// NetworkSpeedMonitor - Advanced speed tracking for dynamic chunk sizing
// Phase 3B: Performance Optimization & Monitoring

import {
  networkStatusService,
  type ConnectionQuality,
} from "./NetworkStatusService";

interface SpeedSample {
  timestamp: number;
  bytesTransferred: number;
  durationMs: number;
  chunkSize: number;
  speedBps: number;
}

interface SpeedPrediction {
  predictedSpeed: number;
  confidence: number; // 0-1
  recommendedChunkSize: number;
  estimatedUploadTime: number; // for remaining bytes
}

interface AdaptiveChunkConfig {
  minChunkSize: number;
  maxChunkSize: number;
  targetUtilization: number; // 0-1, target bandwidth utilization
  windowSize: number; // number of samples to consider
  adaptationThreshold: number; // min speed change % to trigger adaptation
}

class NetworkSpeedMonitor {
  private speedHistory: SpeedSample[] = [];
  private readonly defaultConfig: AdaptiveChunkConfig = {
    minChunkSize: 1 * 1024 * 1024, // 1MB
    maxChunkSize: 50 * 1024 * 1024, // 50MB
    targetUtilization: 0.8, // Use 80% of available bandwidth
    windowSize: 10, // Last 10 samples
    adaptationThreshold: 0.15, // 15% change triggers adaptation
  };

  private config: AdaptiveChunkConfig;
  private readonly speedMovingAverage: number[] = [];
  private readonly speedVariance: number[] = [];

  constructor(config?: Partial<AdaptiveChunkConfig>) {
    this.config = { ...this.defaultConfig, ...config };
  }

  /**
   * Record a speed measurement from an upload chunk
   */
  recordSpeedMeasurement(
    bytesTransferred: number,
    durationMs: number,
    chunkSize: number,
  ): SpeedSample {
    if (durationMs <= 0 || bytesTransferred <= 0) {
      throw new Error("Invalid measurement parameters");
    }

    const speedBps = (bytesTransferred / durationMs) * 1000;
    const sample: SpeedSample = {
      timestamp: Date.now(),
      bytesTransferred,
      durationMs,
      chunkSize,
      speedBps,
    };

    // Add to history
    this.speedHistory.push(sample);

    // Trim to window size
    if (this.speedHistory.length > this.config.windowSize) {
      this.speedHistory.shift();
    }

    // Update moving averages
    this.updateMovingAverages(speedBps);

    // Record with NetworkStatusService for global tracking
    networkStatusService.recordBandwidthMeasurement(
      bytesTransferred,
      durationMs,
    );

    return sample;
  }

  /**
   * Update moving averages and variance calculations
   */
  private updateMovingAverages(newSpeed: number): void {
    this.speedMovingAverage.push(newSpeed);
    if (this.speedMovingAverage.length > this.config.windowSize) {
      this.speedMovingAverage.shift();
    }

    // Calculate variance
    if (this.speedMovingAverage.length >= 3) {
      const mean =
        this.speedMovingAverage.reduce((sum, speed) => sum + speed, 0) /
        this.speedMovingAverage.length;
      const variance =
        this.speedMovingAverage.reduce(
          (sum, speed) => sum + Math.pow(speed - mean, 2),
          0,
        ) / this.speedMovingAverage.length;

      this.speedVariance.push(variance);
      if (this.speedVariance.length > 5) {
        this.speedVariance.shift();
      }
    }
  }

  /**
   * Get current speed prediction based on recent measurements
   */
  getPrediction(remainingBytes?: number): SpeedPrediction {
    if (this.speedHistory.length < 2) {
      // Fallback to NetworkStatusService quality
      const quality = networkStatusService.getConnectionQuality();
      return this.createFallbackPrediction(quality, remainingBytes);
    }

    // Calculate weighted average (recent samples weighted more heavily)
    const weights = this.speedHistory.map((_, index) => Math.pow(2, index));
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

    const weightedSpeed =
      this.speedHistory.reduce(
        (sum, sample, index) => sum + sample.speedBps * weights[index],
        0,
      ) / totalWeight;

    // Calculate confidence based on variance
    const avgVariance =
      this.speedVariance.length > 0
        ? this.speedVariance.reduce((sum, v) => sum + v, 0) /
          this.speedVariance.length
        : 0;
    const confidence = Math.max(
      0.3,
      Math.min(1.0, 1 - avgVariance / (weightedSpeed * weightedSpeed)),
    );

    // Calculate optimal chunk size
    const recommendedChunkSize = this.calculateOptimalChunkSize(
      weightedSpeed,
      confidence,
    );

    // Estimate upload time if remaining bytes provided
    const estimatedUploadTime = remainingBytes
      ? (remainingBytes / (weightedSpeed * this.config.targetUtilization)) *
        1000
      : 0;

    return {
      predictedSpeed: weightedSpeed,
      confidence,
      recommendedChunkSize,
      estimatedUploadTime,
    };
  }

  /**
   * Calculate optimal chunk size based on speed and network conditions
   */
  private calculateOptimalChunkSize(
    speedBps: number,
    confidence: number,
  ): number {
    // Base calculation: aim for 10-30 second upload time per chunk
    const targetTimeMs = confidence > 0.7 ? 20000 : 15000; // More aggressive with high confidence
    let optimalSize =
      (speedBps * targetTimeMs * this.config.targetUtilization) / 1000;

    // Apply bounds
    optimalSize = Math.max(this.config.minChunkSize, optimalSize);
    optimalSize = Math.min(this.config.maxChunkSize, optimalSize);

    // Round to reasonable values (1MB increments for smaller sizes, 5MB for larger)
    if (optimalSize < 10 * 1024 * 1024) {
      optimalSize = Math.round(optimalSize / (1024 * 1024)) * 1024 * 1024; // 1MB increments
    } else {
      optimalSize =
        Math.round(optimalSize / (5 * 1024 * 1024)) * 5 * 1024 * 1024; // 5MB increments
    }

    return optimalSize;
  }

  /**
   * Create fallback prediction when insufficient data available
   */
  private createFallbackPrediction(
    quality: ConnectionQuality,
    remainingBytes?: number,
  ): SpeedPrediction {
    const confidence = 0.5; // Medium confidence for fallback
    const predictedSpeed = quality.uploadSpeed * 0.8; // Conservative estimate

    return {
      predictedSpeed,
      confidence,
      recommendedChunkSize: quality.recommendedChunkSize,
      estimatedUploadTime: remainingBytes
        ? (remainingBytes / predictedSpeed) * 1000
        : 0,
    };
  }

  /**
   * Determine if chunk size should be adapted based on recent performance
   */
  shouldAdaptChunkSize(currentChunkSize: number): boolean {
    if (this.speedHistory.length < 3) return false;

    const prediction = this.getPrediction();
    const sizeDifference =
      Math.abs(prediction.recommendedChunkSize - currentChunkSize) /
      currentChunkSize;

    // Adapt if recommended size differs significantly and we have good confidence
    return (
      sizeDifference > this.config.adaptationThreshold &&
      prediction.confidence > 0.6
    );
  }

  /**
   * Get current network statistics
   */
  getNetworkStats() {
    if (this.speedHistory.length === 0) {
      return {
        averageSpeed: 0,
        lastSpeed: 0,
        samples: 0,
        confidence: 0,
        trend: "unknown" as const,
      };
    }

    const speeds = this.speedHistory.map((s) => s.speedBps);
    const averageSpeed =
      speeds.reduce((sum, speed) => sum + speed, 0) / speeds.length;
    const lastSpeed = speeds[speeds.length - 1];

    // Calculate trend
    let trend: "improving" | "stable" | "degrading" | "unknown" = "stable";
    if (speeds.length >= 3) {
      const recentSpeeds = speeds.slice(-3);
      const firstHalf = recentSpeeds.slice(
        0,
        Math.floor(recentSpeeds.length / 2),
      );
      const secondHalf = recentSpeeds.slice(
        Math.floor(recentSpeeds.length / 2),
      );

      const firstAvg =
        firstHalf.reduce((sum, s) => sum + s, 0) / firstHalf.length;
      const secondAvg =
        secondHalf.reduce((sum, s) => sum + s, 0) / secondHalf.length;

      const changePercent = (secondAvg - firstAvg) / firstAvg;
      if (changePercent > 0.1) trend = "improving";
      else if (changePercent < -0.1) trend = "degrading";
    }

    const prediction = this.getPrediction();

    return {
      averageSpeed,
      lastSpeed,
      samples: this.speedHistory.length,
      confidence: prediction.confidence,
      trend,
      predictedSpeed: prediction.predictedSpeed,
      recommendedChunkSize: prediction.recommendedChunkSize,
    };
  }

  /**
   * Reset speed history (useful when starting new upload)
   */
  reset(): void {
    this.speedHistory = [];
    this.speedMovingAverage.length = 0;
    this.speedVariance.length = 0;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<AdaptiveChunkConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): AdaptiveChunkConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const networkSpeedMonitor = new NetworkSpeedMonitor();

export type { SpeedSample, SpeedPrediction, AdaptiveChunkConfig };
